import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon, Trash2, Repeat, Mic, MessageSquare, Terminal, Keyboard, Plus, Sparkles, Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateItem, useDismissItem } from "@/hooks/useItems";
import { useProjects } from "@/hooks/useProjects";
import { useSubtasks, useCreateSubtask, useUpdateSubtask } from "@/hooks/useSubtasks";
import { useBreakDown } from "@/hooks/useBreakDown";
import { toast } from "sonner";
import type { Item, ItemPriority, ItemRecurrence } from "@/lib/types";
import { cn, formatError } from "@/lib/utils";

type Props = {
  item: Item | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ItemEditSheet({ item, open, onOpenChange }: Props) {
  const updateItem = useUpdateItem();
  const dismissItem = useDismissItem();
  const { data: projects } = useProjects();
  const { data: subtasks = [] } = useSubtasks(item?.id ?? null);
  const createSubtask = useCreateSubtask();
  const updateSubtask = useUpdateSubtask();
  const breakDown = useBreakDown();

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [priority, setPriority] = useState<ItemPriority>("medium");
  const [dateTarget, setDateTarget] = useState<Date | undefined>();
  const [projectTag, setProjectTag] = useState("");
  const [recurring, setRecurring] = useState<ItemRecurrence>(null);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [aiSubtasks, setAiSubtasks] = useState<string[]>([]);

  // Sync state when item changes
  useEffect(() => {
    if (item) {
      setTitle(item.title);
      setNotes(item.notes || "");
      setPriority(item.priority);
      setDateTarget(
        item.date_target
          ? new Date(item.date_target + "T00:00:00")
          : undefined
      );
      setProjectTag(item.project_tag || "");
      setRecurring(item.recurring || null);
    }
  }, [item]);

  if (!item) return null;

  const handleSave = () => {
    updateItem.mutate(
      {
        id: item.id,
        title,
        notes: notes || null,
        priority,
        date_target: dateTarget ? format(dateTarget, "yyyy-MM-dd") : null,
        project_tag: projectTag || null,
        recurring: recurring || null,
      },
      {
        onSuccess: () => {
          toast("Item updated");
          onOpenChange(false);
        },
        onError: (err) => toast.error(formatError(err)),
      }
    );
  };

  const handleDismiss = () => {
    dismissItem.mutate(item.id, {
      onSuccess: () => {
        toast("Item dismissed");
        onOpenChange(false);
      },
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-3/4 sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Item Details</SheetTitle>
          <SheetDescription className="sr-only">
            View item details and edit title, priority, date, project tag, and recurrence.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Context section */}
          <div className="rounded-lg bg-muted/50 p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {item.source === "integration" && <Mic className="h-3 w-3" />}
              {item.source === "claude_code" && <Terminal className="h-3 w-3" />}
              {item.source === "voice" && <MessageSquare className="h-3 w-3" />}
              {item.source === "manual" && <Keyboard className="h-3 w-3" />}
              <span className="capitalize">{item.source.replace("_", " ")}</span>
              <span>&middot;</span>
              <span>{format(new Date(item.created_at), "MMM d, yyyy h:mm a")}</span>
            </div>
            {item.raw_input && item.raw_input !== item.title && (
              <p className="text-sm text-muted-foreground italic">
                {item.raw_input}
              </p>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What needs to happen? Add context, links, or details..."
              rows={6}
              className="text-sm"
            />
          </div>

          {/* Subtasks */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Subtasks</Label>
              {subtasks.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {subtasks.filter((s) => s.status === "done").length}/{subtasks.length} done
                </span>
              )}
            </div>

            {/* Progress bar */}
            {subtasks.length > 0 && (
              <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{
                    width: `${(subtasks.filter((s) => s.status === "done").length / subtasks.length) * 100}%`,
                  }}
                />
              </div>
            )}

            {/* Subtask list */}
            <div className="space-y-1">
              {[...subtasks]
                .sort((a, b) => {
                  if (a.status === "done" && b.status !== "done") return 1;
                  if (a.status !== "done" && b.status === "done") return -1;
                  return 0;
                })
                .map((subtask) => (
                  <div
                    key={subtask.id}
                    className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={subtask.status === "done"}
                      onCheckedChange={(checked) => {
                        updateSubtask.mutate({
                          id: subtask.id,
                          status: checked ? "done" : "active",
                        });
                      }}
                    />
                    <span
                      className={cn(
                        "text-sm flex-1",
                        subtask.status === "done" &&
                          "line-through text-muted-foreground"
                      )}
                    >
                      {subtask.title}
                    </span>
                  </div>
                ))}
            </div>

            {/* Add subtask input */}
            <div className="flex items-center gap-2">
              <Input
                value={newSubtaskTitle}
                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                placeholder="Add a subtask..."
                className="text-sm h-8"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newSubtaskTitle.trim() && item) {
                    createSubtask.mutate({
                      parent_id: item.id,
                      title: newSubtaskTitle.trim(),
                      project_tag: item.project_tag,
                    });
                    setNewSubtaskTitle("");
                  }
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                disabled={!newSubtaskTitle.trim() || createSubtask.isPending}
                onClick={() => {
                  if (newSubtaskTitle.trim() && item) {
                    createSubtask.mutate({
                      parent_id: item.id,
                      title: newSubtaskTitle.trim(),
                      project_tag: item.project_tag,
                    });
                    setNewSubtaskTitle("");
                  }
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* AI breakdown */}
            {item && (
              <div className="space-y-2">
                {aiSubtasks.length === 0 ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs"
                    disabled={breakDown.isPending}
                    onClick={() => {
                      breakDown.mutate(
                        { item_id: item.id, mode: "quick" },
                        {
                          onSuccess: (result) => {
                            if (result.type === "subtasks") {
                              setAiSubtasks(result.subtasks);
                            } else {
                              toast("AI has questions — check notes for context");
                            }
                          },
                          onError: (err) => toast.error(formatError(err)),
                        }
                      );
                    }}
                  >
                    {breakDown.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        Breaking down...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                        Break down with AI
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
                    <p className="text-xs font-medium text-primary">
                      AI suggested subtasks:
                    </p>
                    {aiSubtasks.map((title, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="flex-1">{title}</span>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => {
                          for (const title of aiSubtasks) {
                            createSubtask.mutate({
                              parent_id: item.id,
                              title,
                              project_tag: item.project_tag,
                            });
                          }
                          setAiSubtasks([]);
                          toast(`Added ${aiSubtasks.length} subtasks`);
                        }}
                      >
                        Add All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => setAiSubtasks([])}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label>Priority</Label>
            <RadioGroup
              value={priority}
              onValueChange={(v) => setPriority(v as ItemPriority)}
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="high" id="priority-high" />
                  <Label
                    htmlFor="priority-high"
                    className="text-accent font-medium"
                  >
                    High
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="medium" id="priority-medium" />
                  <Label htmlFor="priority-medium">Medium</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="low" id="priority-low" />
                  <Label
                    htmlFor="priority-low"
                    className="text-muted-foreground"
                  >
                    Low
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label>Target Date</Label>
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateTarget && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateTarget
                      ? format(dateTarget, "MMM d, yyyy")
                      : "No date set"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateTarget}
                    onSelect={setDateTarget}
                  />
                </PopoverContent>
              </Popover>
              {dateTarget && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDateTarget(undefined)}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Project Tag */}
          <div className="space-y-2">
            <Label>Project</Label>
            <Select
              value={projectTag || "__none__"}
              onValueChange={(v) => setProjectTag(v === "__none__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="No project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {projects?.map((project) => (
                  <SelectItem key={project.id} value={project.name}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: project.color }}
                      />
                      {project.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Recurring */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Repeat className="h-3.5 w-3.5" />
              Recurring
            </Label>
            <Select
              value={recurring || "__none__"}
              onValueChange={(v) =>
                setRecurring(v === "__none__" ? null : (v as ItemRecurrence))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Not recurring" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="biweekly">Biweekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
            {recurring && (
              <p className="text-xs text-muted-foreground">
                This item will auto-recreate after completion.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              className="flex-1"
              disabled={updateItem.isPending}
            >
              Save
            </Button>
            <Button
              variant="destructive"
              size="icon"
              onClick={handleDismiss}
              disabled={dismissItem.isPending}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
